<template>
	<div id="item">
		<!-- delete dialog -->
		<v-delete-dialog
			v-model="deleteDialog"
			module-type="items"
			:ids="$route.params.id"
			@on-delete="$emit('update-items', 'delete')"
		>
			{{$T('are-you-sure-you-want-to-$', { message: $T('the-item') })}}
		</v-delete-dialog>

		<div v-if="permissions.items.isReadWrite" class="d-flex mb-2">
			<v-spacer/>
			<template v-if="$route.query.preview">
				<v-btn
					height="32"
					class="me-2"
					color="warning-color white--text"
					key="edit"
					replace
					:to="{ name: 'item', params: { id: $route.params.id } }"
				>
					{{$T('edit')}}
				</v-btn>
				<v-btn
					height="32"
					color="alert-color white--text"
					key="delete"
					@click="deleteDialog = true"
				>
					{{$T('delete')}}
				</v-btn>
			</template>
			<template v-else>
				<v-btn
					height="32"
					class="me-2"
					color="primary"
					key="save"
					:loading="submitLoading"
					@click="submit"
				>
					{{$T('save')}}
				</v-btn>
				<v-btn
					height="32"
					key="cancel"
					:disabled="submitLoading"
					replace
					:to="{
						name: $route.params.id ? 'item' : 'items',
						params: { id: $route.params.id },
						query: { preview: 1 }
					}"
				>
					{{$T('close')}}
				</v-btn>
			</template>
		</div>

		<v-form ref="form" :disabled="Boolean(disableFieldsToPreview) || submitLoading">
			<v-card class="mb-4">
				<v-card-text class="pa-2">
					<p class="text-h6 grey--text text--darken-3 mb-0">
						{{$T('main-info')}}
					</p>
					<v-row dense align="center" class="ma-0">
						<!-- item name -->
						<v-col lg="3" md="4" sm="6" cols="12" class="py-1">
							<v-text-field
								v-model="name"
								:label="$T('inputs.item-name')"
								outlined
								dense
								hide-details
								:loading="loading"
								:rules="rules.required"
							></v-text-field>
						</v-col>

						<!-- parent item -->
						<v-col lg="3" md="4" sm="6" cols="12" class="py-1">
							<v-autocomplete
								v-model="parentItemId"
								:items="items.filter(c => c.type === type.main)"
								item-value="id"
								item-text="name"
								:label="$T('inputs.parent-item')"
								outlined
								dense
								hide-details
								:loading="loading"
								@change="suggestAccountNo()"
							></v-autocomplete>
						</v-col>

						<!-- item type -->
						<v-col lg="3" md="4" sm="6" cols="12" class="py-1">
							<v-autocomplete
								v-model="itemType"
								:items="accountTypes"
								item-value="id"
								item-text="name"
								:label="$T('inputs.type')"
								outlined
								dense
								hide-details
								:loading="loading"
								:rules="rules.requiredAsNumber"
								@change="itemType !== type.sub ? isMix = null : null"
							></v-autocomplete>
						</v-col>
						
						<!-- item no -->
						<v-col lg="3" md="4" sm="6" cols="12" class="py-1">
							<v-text-field
								v-model.number="itemNo"
								type="number"
								min="1"
								:label="$T('inputs.automatic-or-manual-no')"
								hide-spin-buttons
								outlined
								dense
								:prefix="noPrefix"
								:persistent-placeholder="Boolean(noPrefix)"
								hide-details
								:loading="loading || suggestLoading"
								:rules="rules.requiredAsNumber"
							></v-text-field>
						</v-col>
						
						<!-- notes -->
						<v-col :md="itemType === type.sub ? 8 : 9" sm="6" cols="12" class="py-1">
							<v-textarea
								v-model="notes"
								:label="$T('inputs.notes')"
								outlined
								dense
								rows="1"
								auto-grow
								no-resize
								hide-details
								:loading="loading"
							></v-textarea>
						</v-col>

						<!-- is mix -->
						<v-col v-if="itemType === type.sub" md="auto" sm="6" cols="12" class="py-1">
							<v-switch
								v-model="isMix"
								:label="$T('inputs.is-mix')"
								:true-value="true"
								:false-value="null"
								hide-details
								class="ma-0"
								@input="!isMix ? mixItems = [] : null"
							/>
						</v-col>

						<v-col md="auto" sm="6" cols="12" class="py-1">
							<v-switch
								v-if="!isMix"
								v-model="isCommercial"
								:label="$T('inputs.is-commercial')"
								hide-details
								class="ma-0"
							/>
						</v-col>
					</v-row>
				</v-card-text>
			</v-card>

			<!-- mixes Ids -->
			<v-row v-if="isMix" no-gutters>
				<v-col lg="6" md="8" sm="10" cols="12">
					<v-card>
						<v-card-text class="pa-2">
							<p class="text-h6 grey--text text--darken-3 mb-2">
								{{$T('mix-items')}}
							</p>

							<bee-handy-smart-table
								v-model="tableInputs"
								ref="table"
								:headers="headersMix"
								:items="mixItems"
								:items-per-page="-1"
								dense
								:loading="loading"
								:disable-input-fields="loading"
								elevation="0"
								:show-add-row="
									!loading && !Boolean(disableFieldsToPreview) &&
									mixItemsTotalPercent < 100
								"
								editable-rows
								hide-inputs-details
								hide-default-header
								:data-table-props="{ disableSort: true }"
								hide-default-footer
								@keydown.enter="updateEntry"
								@edit-cancelled="editRowIndex = null"
							>
								<template v-slot:body.prepend="{ headers }">
									<tr>
										<td
											v-for="(header, i) in headers"
											:key="i"
											class="text-caption grey--text text--darken-3 font-weight-bold"
										>
											{{header.text}}

											<span
												v-if="header.name === 'percent'"
												class="ms-1"
												:class="{
													'error--text':
														(
															mixItemsTotalPercent
															- (typeof editRowIndex === 'number' ? mixItems[editRowIndex].percent : 0)
															+ (tableInputs.percent || 0) !== 100
														)
												}"
											>
												({{
													mixItemsTotalPercent
													- (typeof editRowIndex === 'number' ? mixItems[editRowIndex].percent : 0)
													+ (tableInputs.percent || 0)
												}})
											</span>
										</td>
									</tr>
								</template>
								
								<!-- item -->
								<template v-slot:input.itemId="{ on, attr }">
									<v-autocomplete
										:items="flatCardamomItems.filter(c => 
											(!mixItems.map(c => c.itemId).includes(c.id) ||
											(editRowIndex !== null ? c.id === mixItems[editRowIndex].itemId : false)) &&  c.itemCategory === itemCategory.commercial
										)"
										:item-text="item => item.itemNo + ' - ' + item.name"
										item-value="id"
										v-on="on"
										v-bind="attr"
									/>
								</template>

								<!-- actions -->
								<template v-slot:item.actions="{ index }">
									<div class="d-flex align-center">
										<v-btn
											icon
											color="warning-color"
											@click="editRowIndex = index; $refs.table.activeEditForRow(index);"
										>
											<v-icon>$edit</v-icon>
										</v-btn>

										<v-btn
											icon
											color="alert-color"
											@click="
												mixItems.splice(index, 1);
												mixItems.forEach((_, i) => {
													if (mixItems[i].id > index) mixItems[i].id--;
												})
											"
										>
											<v-icon>$delete</v-icon>
										</v-btn>
									</div>
								</template>

								<template v-slot:no-input.actions>
									<div class="d-flex align-center">
										<v-btn
											icon
											color="success"
											@click="updateEntry"
										>
											<v-icon>$check</v-icon>
										</v-btn>
									</div>
								</template>
							</bee-handy-smart-table>
						</v-card-text>
					</v-card>
				</v-col>
			</v-row>
		</v-form>
	</div>
</template>

<script>
import rules from '@/helpers/validation-rules'
import { mapGetters, mapState } from 'vuex';
import { types, type, itemCategory } from '@/helpers/enums';
import { getFlatTree } from '@/helpers/functions';

export default {
	name: 'Item',

	data() {
		return {
			loading: false,
			submitLoading: false,
			suggestLoading: false,

			deleteDialog: false,

			noPrefix: null,
			type,
			rules,

			tableInputs: {},
			editRowIndex: null,

			// data
			name: null,
			itemNo: null,
			itemType: null,
			parentItemId: null,
			notes: null,
			isMix: false,
			mixItems: [],
			isCommercial: true,

			accountTypes: types,
			itemCategory
		}
	},

	watch: {
		$route: {
			handler(val, oldVal) {
				this.noPrefix = null;
				if (val.params.id) {
					const item = this.items.find(c => c.id === val.params.id);
					this.name = item.name;
					this.itemNo = item.innerItemNo;
					this.itemType = item.type;
					this.parentItemId = item.parentId;
					this.notes = item.notes;
					this.isMix = item.isMix || null;
					this.isCommercial = item.itemCategory === 0

					const parent = this.getItemById(this.parentItemId);
					this.noPrefix = parent ? parent.itemNo : null;

					// fetch mixes if item is mix
					if (!oldVal || val.params.id !== oldVal.params.id) {
						this.mixItems = [];
						this.loading = true;
						this.$store.dispatch('items/fetchMixItems', {
							itemId: val.params.id
						}).then((data) => {
							const items = data.map((c, i) => ({
								id: i + 1, itemId: c.itemId, percent: c.percent
							}));
							this.mixItems = items;
						}).finally(() => {
							this.loading = false;
						})
					}

					if (val.query.delete) this.deleteDialog = true;
				} else if (this.$route.name === 'item') {
					if (this.$refs.form) this.$refs.form.reset();
					this.mixItems = [];

					if (
						val.query.subOf &&
						this.getItemById(val.query.subOf).type === type.main
					) {
						this.parentItemId = this.$route.query.subOf;
						this.itemType = type.sub;
						this.suggestAccountNo();
					}
				}
			},
			immediate: true
		}
	},

	computed: {
		...mapState({
			permissions: state => state.auth.self.permissions,
			items: state => state.items.items,
			itemsTree: state => state.items.itemsTree,
			cardamomRootId: state => state.configs.itemsConfigs.cardamomRootId,
		}),
		...mapGetters({
			getItemById: 'items/getItemById',
		}),
		
		disableFieldsToPreview() {
			return this.$route.query.preview || this.permissions.items.isReadonly;
		},

		headersMix() {
			const headers = [
				{
					text: this.$T('headers.item'),
					name: 'itemId',
					value: 'itemId',
					width: '55%',
					inputRules: rules.required,
					setValueExpr: val => {
						const item = this.getItemById(val) || {};
						return item.itemNo + ' - ' + item.name;
					}
				},
				{
					text: '%',
					name: 'percent',
					value: 'percent',
					type: 'number',
					inputRules: [
						...rules.requiredAsPercent,
						val => (
							this.mixItemsTotalPercent + val -
							(this.editRowIndex !== null ? this.mixItems[this.editRowIndex].percent : 0)
						) <= 100
					],
				},
			];
			
			if (!this.disableFieldsToPreview) {
				headers.push({
					text: '',
					name: 'actions',
					value: 'actions',
					width: 40,
					noInput: true
				});
			}

			return headers;
		},

		mixItemsTotalPercent() {
			return this.mixItems.reduce((res, a) => res + a.percent, 0);
		},

		flatCardamomItems() {
			return getFlatTree(
				this.cardamomRootId
					? [this.itemsTree.find(c => c.id === this.cardamomRootId)]
					: this.itemsTree
			)
		}
	},

	methods: {
		updateEntry() {
			if (this.$refs.table.validate()) {
				if (this.editRowIndex === null) {
					this.mixItems.push({ id: this.mixItems.length + 1, ...this.tableInputs });
				} else {
					this.mixItems[this.editRowIndex].itemId = this.tableInputs.itemId;
					this.mixItems[this.editRowIndex].percent = this.tableInputs.percent;
					this.$refs.table.resetEdit();
				}

				this.$refs.table.resetInputs();
				if (this.mixItemsTotalPercent < 100) {
					this.$nextTick(() => this.$refs.table.resetInputsFocus());
				}
			}
		},

		suggestAccountNo() {
			const parent = this.getItemById(this.parentItemId);
			this.noPrefix = parent ? parent.itemNo : null;
			
			if (this.parentItemId) {
				this.suggestLoading = true;
				this.$store.dispatch('items/fetchSuggestedNo', {
					parentId: this.parentItemId
				}).then(data => {
					this.itemNo = data;
				}).finally(() => {
					this.suggestLoading = false;
				})
			}
		},

		submit() {
			const isEmptyMixItems = this.isMix && this.mixItems.length === 0;
			if (isEmptyMixItems) {
				this.$eventBus.$emit('show-snackbar', this.$T('messages.mix-items-required'));
			}
			if (!this.$refs.form.validate() || isEmptyMixItems) return;

			this.submitLoading = true;
			const actionName = !this.$route.params.id ? 'create' : 'update';

			this.$store.dispatch(`items/${actionName}`, {
				id: this.$route.params.id,
				name: this.name,
				itemNo: this.itemNo + '',
				innerItemNo: this.itemNo,
				parentId: this.parentItemId,
				type: this.itemType,
				notes: this.notes || '',
				isMix: this.isMix,
				itemCategory: this.isCommercial ? 0 : 1
			}).then((id) => {
				if (this.isMix) {
					this.$store.dispatch('items/updateMixItems', {
						itemId: id || this.$route.params.id,
						items: this.mixItems.map(c => ({ itemId: c.itemId, percent: c.percent }))
					}).then(() => {
						this.$emit('update-items', id || this.$route.params.id);
						this.submitLoading = false;
					});
				} else {
					this.$emit('update-items', id || this.$route.params.id);
				}
			}).finally(() => {
				if (!this.isMix) this.submitLoading = false;
			})
		}
	}
}
</script>

<style lang="scss">
#item {
	.v-text-field__prefix {
		padding: 0;
		font-weight: 500;
		color: #015931;
	}
	.v-input--is-disabled .v-text-field__prefix {
		color: rgba(0, 0, 0, 0.38);
	}
}
</style>